import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
// Type-safe access to the generated proto enum (avoids TS definition friction),
// mirroring the approach used in AccountModal.
const protoModule = require('../../_proto/landscope-map-viewer_pb');
const purposeOptions = [
    { value: 'purchase', label: 'Evaluating land to buy for myself / my family' },
    { value: 'planning', label: 'Planning or designing my own land / homestead' },
    { value: 'client_consulting', label: 'Designing or consulting on land projects for clients' },
    { value: 'multi_site', label: 'Development / portfolio planning across multiple sites' },
    { value: 'teaching', label: 'Teaching, research, or institutional work' },
    { value: 'other', label: 'Other' }
];
const mapPurposeToEnum = (purpose) => {
    const UsagePurpose = protoModule.UsagePurpose;
    switch (purpose) {
        case 'purchase':
            return UsagePurpose.EVALUATING_PROPERTY;
        case 'planning':
            return UsagePurpose.PLANNING_DESIGNING_LAND;
        case 'client_consulting':
            return UsagePurpose.DESIGN_CONSULTING_CLIENTS;
        case 'multi_site':
            return UsagePurpose.MULTI_SITE_DEVELOPMENT;
        case 'teaching':
            return UsagePurpose.TEACHING_RESEARCH_INSTITUTIONAL;
        case 'other':
            return UsagePurpose.OTHER;
        default:
            return UsagePurpose.USAGE_PURPOSE_UNSPECIFIED;
    }
};
/**
 * One-time prompt that asks the user their use case after the fact. It only
 * appears for authenticated users the server has flagged as never having told
 * us (e.g. Google sign-ups that bypassed the signup form). Saving clears the
 * flag server-side, so refreshing the user unmounts this.
 */
export const UsagePurposePrompt = () => {
    const { isAuthenticated, user, getCurrentUser } = useAuth();
    const [purpose, setPurpose] = useState('');
    const [otherPurpose, setOtherPurpose] = useState('');
    const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    if (!isAuthenticated || !(user === null || user === void 0 ? void 0 : user.needsUsagePurpose)) {
        return null;
    }
    const save = async (usagePurposeEnum, description) => {
        setIsSubmitting(true);
        setError('');
        try {
            await authService.setUsagePurpose(usagePurposeEnum, description, subscribeToNewsletter);
            // Refresh the user so needsUsagePurpose clears and this unmounts.
            await getCurrentUser();
        }
        catch (err) {
            console.error('Failed to save usage purpose:', err);
            setError('Could not save. Please try again.');
            setIsSubmitting(false);
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!purpose) {
            setError('Please choose an option.');
            return;
        }
        if (purpose === 'other' && !otherPurpose.trim()) {
            setError('Please tell us a bit more.');
            return;
        }
        await save(mapPurposeToEnum(purpose), purpose === 'other' ? otherPurpose.trim() : '');
    };
    // Records a distinct "declined" value so the flag clears for good (no re-prompt)
    // while keeping the answer out of the real use-case data.
    const handleDecline = async () => {
        await save(protoModule.UsagePurpose.PREFER_NOT_TO_SAY, '');
    };
    return (_jsxs("div", { className: "fixed inset-0 z-[75] flex items-center justify-center p-4", children: [_jsx("div", { className: "ls-modal-backdrop" }), _jsxs("div", { className: "ls-modal-card ls-modal-card-animate relative w-full max-w-md flex flex-col", style: { maxHeight: '92vh' }, children: [_jsx("div", { className: "p-6 ls-modal-band-top shrink-0", style: { backgroundColor: 'var(--forest)' }, children: _jsx("h2", { className: "text-xl font-semibold", style: { color: 'var(--sand)' }, children: "One quick question" }) }), _jsxs("div", { className: "p-6 overflow-y-auto min-h-0 flex-1 ls-modal-band-bottom", style: { backgroundColor: 'var(--sand)' }, children: [_jsx("p", { className: "text-sm mb-4", style: { color: 'var(--charcoal)' }, children: "Help us tailor LandScope to you \u2014 what best describes how you'll use it?" }), error && (_jsx("div", { className: "mb-4 p-3 rounded-lg bg-red-50 border border-red-200", children: _jsx("p", { className: "text-red-600 text-sm", children: error }) })), _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("select", { value: purpose, onChange: (e) => {
                                            setPurpose(e.target.value);
                                            if (error)
                                                setError('');
                                        }, disabled: isSubmitting, className: "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50", style: { borderColor: 'var(--pewter)', backgroundColor: 'white', color: 'var(--charcoal)' }, children: [_jsx("option", { value: "", children: "Select an option" }), purposeOptions.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value)))] }), purpose === 'other' && (_jsx("textarea", { value: otherPurpose, onChange: (e) => setOtherPurpose(e.target.value), rows: 3, disabled: isSubmitting, className: "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 resize-none", style: { borderColor: 'var(--pewter)', backgroundColor: 'white', color: 'var(--charcoal)' }, placeholder: "Tell us how you plan to use LandScope..." })), _jsxs("label", { className: "flex items-start gap-2 cursor-pointer select-none", children: [_jsx("input", { type: "checkbox", checked: subscribeToNewsletter, onChange: (e) => setSubscribeToNewsletter(e.target.checked), disabled: isSubmitting, className: "mt-0.5 h-4 w-4 flex-shrink-0 rounded cursor-pointer", style: { accentColor: 'var(--forest)' } }), _jsx("span", { className: "text-xs", style: { color: 'var(--charcoal)' }, children: "Subscribe to the LandScope Letters. The newsletter from the founder of LandScope drops weekly tips, tricks, tool releases, updates, and deals." })] }), _jsx("button", { type: "submit", disabled: isSubmitting, className: "w-full py-2.5 px-4 rounded-full font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: isSubmitting ? 'Saving…' : 'Save' }), _jsx("div", { className: "text-center", children: _jsx("button", { type: "button", onClick: handleDecline, disabled: isSubmitting, className: "text-sm hover:underline disabled:opacity-50", style: { color: 'var(--pewter)' }, children: "Prefer not to say" }) })] })] })] })] }));
};
